import { DocumentScannerOutlined, Person } from '@mui/icons-material';
import { LinearProgress, ListItem, ListItemIcon, ListItemText, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { ReportParametersInput, useGetReportCountQuery } from 'gql';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

export const ReportCountPaper: React.FC = () => {
  const { formatMessage } = useIntl();

  const { control, getValues } = useFormContext<ReportParametersInput>();

  const formValues = useWatch({ control, name: 'filters' });
  const includeUnknownIndividuals = useWatch({ control, name: 'includeUnknownIndividuals' });

  const fromDate = dayjs(formValues?.period?.from);
  const toDate = dayjs(formValues?.period?.to);
  const hasPeriod = fromDate.isValid() && toDate.isValid();

  const { data: reportCountData, isFetching: isReportCountFetching, refetch } = useGetReportCountQuery({
    input: {
      ...getValues(),
      filters: {
        ...getValues().filters,
        period: {
          from: hasPeriod ? fromDate.local().startOf('day').toISOString() : '',
          to: hasPeriod ? toDate.local().endOf('day').toISOString() : ''
        }
      }
    }
  }, {
    enabled: hasPeriod,
    refetchOnMount: true,
    staleTime: 0,
    onError: () => {
      // Do nothing
    }
  });

  useEffect(() => {
    if (hasPeriod) {
      refetch();
    }
  }, [hasPeriod, includeUnknownIndividuals, refetch]);

  const { activitiesCount, individualsCount } = reportCountData?.reportCount ?? {};

  const errorMessage = () => {
    if (!hasPeriod) {
      return formatMessage({ id: 'Select a start and end date' });
    }
  };

  return (
    <Paper>
      <Stack>
        <LinearProgress style={{ visibility: isReportCountFetching ? 'visible' : 'hidden' }} />
        <ListItem divider={Boolean(individualsCount)}>
          <ListItemIcon>
            <DocumentScannerOutlined />
          </ListItemIcon>
          <ListItemText
            primary={activitiesCount ?? errorMessage()}
            secondary={formatMessage({ id: 'Activities' })}
          />
        </ListItem>

        {individualsCount != null && (
          <ListItem>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText
              primary={individualsCount}
              secondary={formatMessage({ id: 'Individuals' })}
            />
          </ListItem>
        )}
      </Stack>
    </Paper>
  );
};
